import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { renderInvoicePdf, renderInvoicePdfBase64 } from "../lib/pdf";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { InvoiceDocument } from "../components/InvoiceDocument";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Upload, Loader2, Download, CheckCircle2, ImageIcon, Mail, AlertCircle } from "lucide-react";

export default function InvoiceGenerator() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [invoice, setInvoice] = useState(null); // saved invoice
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | sending | sent | error
  const [emailRecipients, setEmailRecipients] = useState([]);

  const [form, setForm] = useState({
    meter_number: "",
    meter_reading: "",
    reading_date: new Date().toISOString().slice(0, 10),
    results: "",
    technician_name: user?.name || "",
    urgent: false,
    photo_path: null,
  });

  useEffect(() => {
    api
      .get(`/jobs/${jobId}`)
      .then(({ data }) => {
        setJob(data);
        setForm((f) => ({ ...f, urgent: data.service_type === "urgent" }));
      })
      .catch(() => setError("Job not found"));
  }, [jobId]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_path: data.path }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveInvoice = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/invoices", {
        job_id: jobId,
        ...form,
      });
      setInvoice(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not create invoice");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      const pdf = await renderInvoicePdf(invoiceRef.current);
      pdf.save(`${invoice?.invoice_number || "invoice"}.pdf`);
    } catch (err) {
      setError("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async (inv) => {
    if (!invoiceRef.current) return;
    setEmailStatus("sending");
    try {
      const pdf_base64 = await renderInvoicePdfBase64(invoiceRef.current);
      const { data } = await api.post(`/invoices/${inv.id}/email`, { pdf_base64 });
      if (data.disabled) {
        setEmailStatus("disabled");
        return;
      }
      setEmailRecipients(data.recipients || []);
      setEmailStatus("sent");
    } catch (err) {
      setEmailStatus("error");
    }
  };

  // Auto-email the invoice to solicitor + contractor once it is created.
  useEffect(() => {
    if (invoice && emailStatus === "idle") {
      const t = setTimeout(() => sendEmail(invoice), 700);
      return () => clearTimeout(t);
    }
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !job) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-500">{error}</div>
      </div>
    );
  }

  const previewData = invoice || {
    invoice_number: "PREVIEW",
    date: new Date().toLocaleDateString("en-AU"),
    matter_number: job?.matter_number,
    purchaser_name: job?.purchaser_name,
    vendor_name: job?.vendor_name,
    property_address: job?.property_address,
    property_description: job?.property_description,
    property_type: job?.property_type,
    settlement_date: job?.settlement_date,
    preferred_reading_date: job?.preferred_reading_date,
    basic_fee: 115,
    urgent_fee: form.urgent ? 35 : 0,
    ...form,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link
          to="/contractor"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: form */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-400">Generate Invoice</span>
            <h1 className="font-display text-2xl font-600 tracking-tight text-slate-900 mt-1 mb-1">
              {job?.property_address || "…"}
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              Matter {job?.matter_number} • Purchaser {job?.purchaser_name}
            </p>

            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
              {/* Photo upload */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Photo of water meter</Label>
                <label
                  data-testid="photo-dropzone"
                  className="mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg py-8 cursor-pointer hover:border-sky-400 hover:bg-sky-50/40 transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                  ) : form.photo_path ? (
                    <>
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                      <span className="text-sm text-emerald-600 font-medium">Photo uploaded — click to replace</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400" />
                      <span className="text-sm text-slate-500">Click to upload a photo</span>
                    </>
                  )}
                  <input
                    data-testid="photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Meter #</Label>
                  <Input data-testid="meter-number-input" value={form.meter_number} onChange={update("meter_number")} placeholder="e.g. A2295831" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Actual reading</Label>
                  <Input data-testid="meter-reading-input" value={form.meter_reading} onChange={update("meter_reading")} placeholder="e.g. 04821 kL" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Reading date</Label>
                <Input data-testid="reading-date-input" type="date" value={form.reading_date} onChange={update("reading_date")} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Results / notes</Label>
                <Textarea data-testid="results-input" value={form.results} onChange={update("results")} rows={3} placeholder="Reading taken, meter in working order…" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Technician name</Label>
                <Input data-testid="technician-input" value={form.technician_name} onChange={update("technician_name")} placeholder="Fletcher Pukall" />
              </div>

              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4 border border-slate-100">
                <div>
                  <div className="text-sm font-medium text-slate-800">Urgent fee (+$35)</div>
                  <div className="text-xs text-slate-500">Basic fee $115 always applies.</div>
                </div>
                <Switch
                  data-testid="urgent-switch"
                  checked={form.urgent}
                  onCheckedChange={(v) => setForm({ ...form, urgent: v })}
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="text-sm text-slate-500">
                  Total: <span className="font-display font-700 text-lg text-slate-900">${115 + (form.urgent ? 35 : 0)}</span>
                </div>
              </div>

              {error && <p data-testid="invoice-error" className="text-sm text-red-500">{error}</p>}

              {!invoice ? (
                <Button
                  data-testid="create-invoice-btn"
                  onClick={saveInvoice}
                  disabled={saving}
                  className="w-full bg-sky-500 hover:bg-sky-600 h-11 text-base"
                >
                  {saving ? "Generating…" : "Auto-generate invoice"}
                </Button>
              ) : (
                <div className="flex flex-col gap-3">
                  <div data-testid="invoice-success" className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" /> Invoice {invoice.invoice_number} generated.
                  </div>

                  {/* Email status */}
                  <div
                    data-testid="email-status"
                    className={`flex items-start gap-2 rounded-lg p-3 text-sm border ${
                      emailStatus === "sent"
                        ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                        : emailStatus === "error"
                        ? "bg-red-50 border-red-100 text-red-600"
                        : emailStatus === "disabled"
                        ? "bg-amber-50 border-amber-100 text-amber-700"
                        : "bg-sky-50 border-sky-100 text-sky-700"
                    }`}
                  >
                    {emailStatus === "sending" && <Loader2 className="w-4 h-4 mt-0.5 animate-spin shrink-0" />}
                    {emailStatus === "sent" && <Mail className="w-4 h-4 mt-0.5 shrink-0" />}
                    {emailStatus === "disabled" && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                    {emailStatus === "error" && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                    <div>
                      {emailStatus === "sending" && "Emailing the invoice & certificate…"}
                      {emailStatus === "sent" && (
                        <>
                          Emailed to:{" "}
                          <span className="font-medium">{emailRecipients.join(", ")}</span>
                        </>
                      )}
                      {emailStatus === "disabled" && "Email sending is turned off (test mode). The invoice was saved but not emailed."}
                      {emailStatus === "error" && (
                        <div className="flex items-center gap-2">
                          <span>Couldn't send the email.</span>
                          <button
                            data-testid="retry-email-btn"
                            onClick={() => sendEmail(invoice)}
                            className="underline font-medium"
                          >
                            Retry
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={downloading} className="flex-1 bg-slate-900 hover:bg-slate-800 h-11">
                      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                      Download PDF
                    </Button>
                    <Button data-testid="goto-dashboard-btn" variant="outline" onClick={() => navigate("/contractor")} className="flex-1 h-11">
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Right: live preview */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="flex items-center gap-2 text-slate-400 mb-3">
              <ImageIcon className="w-4 h-4" />
              <span className="text-xs tracking-[0.15em] uppercase font-semibold">Live Invoice Preview</span>
            </div>
            <div className="bg-slate-100 rounded-xl p-4 sm:p-6 border border-slate-200">
              <InvoiceDocument ref={invoiceRef} data={previewData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
